import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { HwcgRosterActivationFields } from 'components/IncidentConfig/CreateIncident/HwcgRosterActivationFields';
import {
  createEmptyHwcgEocNotice,
  type HwcgEocNoticeState,
} from 'components/IncidentConfig/CreateIncident/hwcgEocNotice';
import { hwcgRosterActivationErrors } from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import type { HwcgIncidentSpecificsState } from 'components/IncidentConfig/CreateIncident/hwcgIncidentSpecifics';
import apiConfig from 'constants/apiConfig';
import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { useSendSeatActivationRequests } from 'hooks/incident/useRoster';
import { selectUserGuid } from 'slices/commonSelectors';

import { useHwcgRosterActivation } from './hwcgRosterActivationContext';
import { unsentSeatCount } from './rosterPeriods';

const BLANK_SPECIFICS: Pick<
  HwcgIncidentSpecificsState,
  'time' | 'date' | 'companyName' | 'wellName' | 'blockNumber' | 'waterDepthFt'
> = {
  time: '',
  date: '',
  companyName: '',
  wellName: '',
  blockNumber: '',
  waterDepthFt: '',
};

function asNotice(value: unknown): HwcgEocNoticeState {
  const empty = createEmptyHwcgEocNotice();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return empty;
  const row = value as Partial<HwcgEocNoticeState>;
  return {
    ...empty,
    ...row,
    recipientContactIds: Array.isArray(row.recipientContactIds)
      ? row.recipientContactIds.filter((id) => typeof id === 'string')
      : [],
    channels:
      Array.isArray(row.channels) && row.channels.length > 0
        ? (row.channels as HwcgEocNoticeState['channels'])
        : empty.channels,
    locationLabel:
      typeof row.locationLabel === 'string' ? row.locationLabel : '',
    reportToTime: typeof row.reportToTime === 'string' ? row.reportToTime : '',
  };
}

/**
 * Optional activation-request editor for an existing HWCG roster, plus a send
 * to every assignee on the selected period's roster not yet sent a request.
 */
export function HwcgRosterActivationPanel({
  incidentId,
  groupId,
  plannedPeriodId,
  periodLabel,
  members,
}: {
  incidentId: string;
  groupId: string;
  /** The planned period the roster view shows; null for the current period. */
  plannedPeriodId: string | null;
  periodLabel: string;
  members: RosterEntry[];
}) {
  const activation = useHwcgRosterActivation();
  const send = useSendSeatActivationRequests(incidentId, groupId);
  const viewerGuid = useSelector(selectUserGuid);
  const unsent = unsentSeatCount(members, { excludeUserId: viewerGuid });
  const saved = useQuery({
    queryKey: ['hwcg-group-actions', incidentId],
    queryFn: async () => {
      const response = await axios.get(
        apiConfig.apiGateway.hwcgIncidentGroupActions(incidentId)
      );
      return response.data as {
        specifics?: Partial<HwcgIncidentSpecificsState>;
        notices?: { responsible_party?: unknown };
      };
    },
    enabled: Boolean(incidentId),
  });

  const replaceNotice = activation?.replaceNotice;
  useEffect(() => {
    const notice = saved.data?.notices?.responsible_party;
    if (!notice || !replaceNotice) return;
    replaceNotice(asNotice(notice));
  }, [replaceNotice, saved.data]);

  if (!activation || !groupId) return null;

  const specifics = {
    ...BLANK_SPECIFICS,
    ...saved.data?.specifics,
  };
  const errors = activation.customize
    ? hwcgRosterActivationErrors(activation.notice)
    : [];

  return (
    <div className="dark tw:mb-3 tw:flex tw:flex-col tw:gap-3">
      <HwcgRosterActivationFields
        enabled={activation.customize}
        onEnabledChange={activation.setCustomize}
        notice={activation.notice}
        onNoticeChange={activation.setNotice}
        specifics={specifics}
        checkboxLabel="Customize activation requests"
        idPrefix="live-roster"
      />
      {errors.length > 0 ? (
        <p className="tw:m-0 tw:text-xs tw:text-destructive">
          {errors.join(' ')}
        </p>
      ) : null}
      <div className="tw:flex tw:items-center tw:gap-2">
        <Button
          type="button"
          size="sm"
          disabled={send.isPending || errors.length > 0 || unsent === 0}
          onClick={() =>
            send.mutate({
              plannedPeriodId,
              hwcgRosterNotice: activation.rosterNotice,
            })
          }
        >
          {send.isPending
            ? 'Sending…'
            : `Send Activation Request to All Not Yet Sent Request (${unsent})`}
        </Button>
        <span className="tw:text-xs tw:text-muted-foreground">
          {periodLabel}
        </span>
      </div>
    </div>
  );
}
